import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useApolloClient } from 'react-apollo';

import Button from '../../../../../../../components/global/Button';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import getAccountMarketingMaterialAssetDownloadUrls from '../../graphql/getAccountMarketingMaterialAssetDownloadUrls';
import Spinner from '../../../../../../../components/global/Spinner';
import useIsNativeWebView from '../../../../../../../services/native-bridge/hooks/useIsWebView';
import { postNativeAppAssetDownload } from '../../../../../../../services/native-bridge/native-apps-interface';

const propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  label: PropTypes.node,
  accountMarketingMaterialId: PropTypes.string.isRequired,
  assetKey: PropTypes.string.isRequired,
};

const AssetDownloadButton = ({
  className,
  disabled,
  icon,
  label,
  accountMarketingMaterialId,
  assetKey,
  ...props
}) => {
  const classes = classNames({
    [className]: !!className,
  });
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const openAlert = useOpenAlert();
  const client = useApolloClient();
  const isNativeWebView = useIsNativeWebView();

  const onDownload = async () => {
    if (downloadUrl) {
      if (isNativeWebView) {
        // post
        postNativeAppAssetDownload({ downloadUrl });
      }
      return;
    }

    try {
      setLoading(true);
      const response = await client.query({
        query: getAccountMarketingMaterialAssetDownloadUrls,
        variables: {
          id: accountMarketingMaterialId,
        },
      });

      const url = response.data.accountMarketingMaterial[assetKey];
      setDownloadUrl(url);
      if (isNativeWebView) {
        postNativeAppAssetDownload({ downloadUrl: url });
      } else {
        ref.current.click();
      }
    } catch (error) {
      openAlert({
        message: 'There was an error while downloading your marketing asset.',
        info: getErrorString(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      className={classes}
      disabled={disabled || loading}
      href={downloadUrl && !isNativeWebView ? downloadUrl : undefined}
      onClick={downloadUrl && !isNativeWebView ? undefined : onDownload}
      inputRef={ref}
      download
      {...props}
    >
      {loading ? <Spinner light size="xs" className="mr-1" /> : icon}
      {label}
    </Button>
  );
};

AssetDownloadButton.propTypes = propTypes;

export default AssetDownloadButton;
